import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search, Loader2, Download, ArrowRightCircle, CheckCircle2, XCircle,
  RotateCcw, Smartphone, Landmark, Receipt, Copy,
} from "lucide-react";
import { adminApi, errMsg } from "@/lib/api";
import { fmtMoney, fmtDate, timeAgo, TXN_LABELS, downloadCSV } from "@/lib/format";
import { PageHeader, StatusBadge, EmptyState, CopyButton, useDebounced } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const SELL_STATUSES = ["Pending", "Processing", "Paid", "Rejected", "Refunded"];
const PAGE = 25;

const ACTIONS = {
  Pending: ["Processing", "Rejected"],
  Processing: ["Paid", "Rejected"],
  Paid: ["Refunded"],
};
const ACTION_LABEL = {
  Processing: "Mark processing",
  Paid: "Mark paid",
  Rejected: "Reject & refund",
  Refunded: "Refund USDT",
};
const ACTION_STYLE = {
  Paid: "bg-emerald-700 hover:bg-emerald-800 text-white",
  Rejected: "bg-rose-700 hover:bg-rose-800 text-white",
  Refunded: "bg-violet-700 hover:bg-violet-800 text-white",
};
const METHOD_LABEL = {
  upi: { label: "UPI", Icon: Smartphone },
  bank: { label: "Bank", Icon: Landmark },
  imps_note: { label: "IMPS/RTGS", Icon: Receipt },
};

function PayoutSummary({ order }) {
  if (!order) return null;
  const d = order.payout_details || {};
  if (order.payout_method === "upi") {
    return (
      <div className="space-y-1 text-sm">
        <p><span className="text-muted-foreground">UPI ID: </span><span className="num font-medium">{d.upi_id}</span></p>
        {d.name && <p><span className="text-muted-foreground">Name: </span>{d.name}</p>}
      </div>
    );
  }
  if (order.payout_method === "bank") {
    return (
      <div className="space-y-1 text-sm">
        <p><span className="text-muted-foreground">Name: </span>{d.account_name}</p>
        <p><span className="text-muted-foreground">Account: </span><span className="num font-medium">{d.account_number}</span></p>
        <p><span className="text-muted-foreground">IFSC: </span><span className="num font-medium">{d.ifsc}</span>{d.bank_name ? ` · ${d.bank_name}` : ""}</p>
      </div>
    );
  }
  return (
    <p className="whitespace-pre-wrap text-sm" data-testid="admin-sell-payout-note">{d.note}</p>
  );
}

function ActionDialog({ order, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [payoutRef, setPayoutRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  React.useEffect(() => {
    if (action) { setNote(""); setErr(""); setPayoutRef(""); }
  }, [action]);
  if (!order || !action) return null;
  const needsNote = ["Rejected", "Refunded"].includes(action);
  const needsPayoutRef = action === "Paid";
  const go = async () => {
    if (needsNote && !note.trim()) return setErr("Please enter a reason");
    if (needsPayoutRef && !payoutRef.trim()) return setErr("Enter the payout reference (UTR / txn id)");
    setBusy(true);
    setErr("");
    try {
      await adminApi.post(`/admin/sell/${order.id}/status`, {
        status: action,
        note: note.trim() || null,
        admin_payout_ref: needsPayoutRef ? payoutRef.trim() : null,
      });
      toast.success(`Sell order ${order.id}: ${action}`);
      onDone();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-sell-action-dialog">
        <DialogHeader>
          <DialogTitle>{ACTION_LABEL[action]} · {order.id}</DialogTitle>
          <DialogDescription>
            {action === "Paid"
              ? "Mark this order as paid once INR has been sent to the user. Enter the UTR / payment reference."
              : action === "Rejected"
              ? "USDT will be returned to the user wallet with a refund ledger entry."
              : action === "Refunded"
              ? "Already-paid order will be reversed. USDT credited back; INR already paid is NOT recovered automatically."
              : "User will be notified of this status change."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-secondary p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">USDT sold</span>
              <span className="num">{fmtMoney(order.amount_usdt, "USDT")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">INR payable</span>
              <span className="num font-semibold">{fmtMoney(order.inr_payable, "INR")}</span>
            </div>
          </div>
          {needsPayoutRef && (
            <div className="space-y-1.5">
              <Label>Payout reference (UTR / Txn ID)</Label>
              <Input
                value={payoutRef}
                onChange={(e) => setPayoutRef(e.target.value)}
                placeholder="e.g. 412345678901"
                className="num"
                data-testid="admin-sell-payout-ref-input"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{needsNote ? "Reason (shown to user)" : "Internal note (optional)"}</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="admin-sell-note-input"
            />
          </div>
          {err && <p className="text-sm text-rose-700" data-testid="admin-sell-action-error">{err}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="admin-sell-action-cancel">Cancel</Button>
          <Button
            onClick={go}
            disabled={busy}
            className={ACTION_STYLE[action]}
            data-testid="admin-sell-action-confirm"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {ACTION_LABEL[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailSheet({ id, onClose }) {
  const qc = useQueryClient();
  const [action, setAction] = useState(null);
  const { data: o, isLoading } = useQuery({
    queryKey: ["admin-sell-order", id],
    queryFn: () => adminApi.get(`/admin/sell/${id}`).then((r) => r.data),
    enabled: !!id,
  });
  const done = () => {
    setAction(null);
    qc.invalidateQueries({ queryKey: ["admin-sell-order", id] });
    qc.invalidateQueries({ queryKey: ["admin-sell-list"] });
  };
  return (
    <Sheet open={!!id} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="admin-sell-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Sell order {o && <StatusBadge status={o.status} />}
          </SheetTitle>
        </SheetHeader>
        {isLoading || !o ? (
          <Skeleton className="mt-6 h-96" />
        ) : (
          <div className="mt-4 space-y-6">
            {ACTIONS[o.status] && (
              <div className="flex flex-wrap gap-2" data-testid="admin-sell-actions">
                {ACTIONS[o.status].map((a) => (
                  <Button
                    key={a}
                    size="sm"
                    variant={ACTION_STYLE[a] ? "default" : "outline"}
                    className={ACTION_STYLE[a]}
                    onClick={() => setAction(a)}
                    data-testid={`admin-sell-action-${a.toLowerCase()}`}
                  >
                    {a === "Paid" && <CheckCircle2 className="mr-1 h-4 w-4" />}
                    {a === "Rejected" && <XCircle className="mr-1 h-4 w-4" />}
                    {a === "Refunded" && <RotateCcw className="mr-1 h-4 w-4" />}
                    {a === "Processing" && <ArrowRightCircle className="mr-1 h-4 w-4" />}
                    {ACTION_LABEL[a]}
                  </Button>
                ))}
              </div>
            )}
            <div className="space-y-3 rounded-xl border bg-card p-4 text-sm">
              <Field label="Order ID">
                <span className="inline-flex items-center gap-1">
                  <span className="num font-medium" data-testid="admin-sell-id">{o.id}</span>
                  <CopyButton value={o.id} label="" variant="ghost" className="h-7 w-7 p-0" testId="admin-sell-copy-id" />
                </span>
              </Field>
              <Field label="User">
                {o.user_name}
                <span className="block text-xs text-muted-foreground">{o.user_email}</span>
              </Field>
              <Field label="Amount sold"><span className="num">{fmtMoney(o.amount_usdt, "USDT")}</span></Field>
              <Field label="Rate"><span className="num">{fmtMoney(o.rate, "INR")}</span></Field>
              <Field label="Gross INR"><span className="num">{fmtMoney(o.gross_inr, "INR")}</span></Field>
              <Field label="Fee"><span className="num">{fmtMoney(o.fee_amount, "INR")} ({o.fee_pct}%)</span></Field>
              <Field label="INR payable">
                <span className="num font-semibold text-foreground">{fmtMoney(o.inr_payable, "INR")}</span>
              </Field>
              <Field label="Submitted">
                {fmtDate(o.created_at)}
                <span className="text-xs text-muted-foreground"> ({timeAgo(o.created_at)})</span>
              </Field>
              {o.verified_by && (
                <Field label="Verified by">
                  {o.verified_by}
                  <span className="block text-xs text-muted-foreground">{fmtDate(o.verified_at)}</span>
                </Field>
              )}
              {o.admin_payout_ref && (
                <Field label="Payout reference">
                  <span className="inline-flex items-center gap-1">
                    <span className="num" data-testid="admin-sell-payout-ref">{o.admin_payout_ref}</span>
                    <CopyButton value={o.admin_payout_ref} label="" variant="ghost" className="h-7 w-7 p-0" testId="admin-sell-copy-payout-ref" />
                  </span>
                </Field>
              )}
              {o.status_reason && (
                <Field label="Reason"><span className="text-rose-700">{o.status_reason}</span></Field>
              )}
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold">Payout destination</p>
              <div className="rounded-xl border bg-card p-4">
                <PayoutSummary order={o} />
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold">History</p>
              <ul className="space-y-2" data-testid="admin-sell-history">
                {o.history?.slice().reverse().map((h, i) => (
                  <li key={i} className="rounded-lg border p-3 text-sm">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={h.status} />
                      <span className="ml-auto text-xs text-muted-foreground">{fmtDate(h.at)}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      by {h.by} ({h.source}){h.note ? ` — ${h.note}` : ""}
                    </p>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold">Ledger entries</p>
              {!o.ledger?.length ? (
                <p className="text-sm text-muted-foreground">No wallet entries yet.</p>
              ) : (
                <ul className="divide-y rounded-lg border" data-testid="admin-sell-ledger">
                  {o.ledger.map((t) => (
                    <li key={t.id} className="flex justify-between px-3 py-2 text-sm">
                      <span>{TXN_LABELS[t.transaction_type] || t.transaction_type}</span>
                      <span className={cn("num", t.amount >= 0 ? "text-emerald-700" : "text-rose-700")}>
                        {t.amount >= 0 ? "+" : "−"}{fmtMoney(Math.abs(t.amount), t.currency)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        <ActionDialog order={o} action={action} onClose={() => setAction(null)} onDone={done} />
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b py-2 last:border-0">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

export default function AdminSellOrders() {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState(null);
  const s = useDebounced(search, 350);
  const params = { status: status === "all" ? undefined : status, search: s || undefined };
  const { data, isLoading } = useQuery({
    queryKey: ["admin-sell-list", params, page],
    queryFn: () =>
      adminApi.get("/admin/sell", { params: { ...params, limit: PAGE, skip: page * PAGE } }).then((r) => r.data),
    refetchInterval: 15000,
  });
  const exportCsv = async () => {
    const r = await adminApi.get("/admin/sell", { params: { ...params, limit: 500 } });
    downloadCSV(
      `sell-orders-${Date.now()}.csv`,
      r.data.items.map((o) => ({
        order_id: o.id,
        user_email: o.user_email,
        amount_usdt: o.amount_usdt,
        rate: o.rate,
        gross_inr: o.gross_inr,
        fee_amount: o.fee_amount,
        inr_payable: o.inr_payable,
        payout_method: o.payout_method,
        payout: JSON.stringify(o.payout_details || {}),
        status: o.status,
        verified_by: o.verified_by || "",
        verified_at: o.verified_at || "",
        admin_payout_ref: o.admin_payout_ref || "",
        created_at: o.created_at,
      }))
    );
  };
  const tabs = [["all", "All", data?.items?.length ? Object.values(data?.counts || {}).reduce((a, b) => a + b, 0) : 0], ...SELL_STATUSES.map((x) => [x, x, data?.counts?.[x]])];

  return (
    <div data-testid="admin-sell-orders-page">
      <PageHeader
        title="Sell orders"
        subtitle="Review user cash-out requests. USDT is already held in the user wallet; confirm your INR payout before marking as paid."
        actions={
          <Button variant="outline" className="gap-2 bg-white" onClick={exportCsv} data-testid="admin-sell-export-csv">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        }
      />
      <div className="mb-3 flex gap-1 overflow-x-auto scrollbar-thin pb-1" data-testid="admin-sell-tabs">
        {tabs.map(([k, l, n]) => (
          <button
            key={k}
            onClick={() => { setStatus(k); setPage(0); }}
            data-testid={`admin-sell-status-tab-${k.toLowerCase()}`}
            className={cn(
              "flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
              status === k ? "border-primary bg-primary text-white" : "bg-white hover:bg-secondary"
            )}
          >
            {l}
            <span className={cn("num rounded-full px-1.5 text-[11px]", status === k ? "bg-white/20" : "bg-secondary")}>
              {n ?? 0}
            </span>
          </button>
        ))}
      </div>
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search order ID, user email or payout reference"
            className="h-10 bg-white pl-9"
            data-testid="admin-sell-search-input"
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-80 rounded-xl" />
      ) : !data?.items?.length ? (
        <EmptyState title="No sell orders match" testId="admin-sell-empty">
          Nothing to review right now. New orders appear here automatically.
        </EmptyState>
      ) : (
        <Card className="overflow-hidden">
          <div className="max-h-[70vh] overflow-auto scrollbar-thin">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-white">
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Payout</TableHead>
                  <TableHead className="text-right">USDT</TableHead>
                  <TableHead className="text-right">INR</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((o) => {
                  const M = METHOD_LABEL[o.payout_method] || { label: o.payout_method, Icon: Receipt };
                  const Icon = M.Icon;
                  return (
                    <TableRow
                      key={o.id}
                      className="cursor-pointer hover:bg-muted/60"
                      onClick={() => setOpenId(o.id)}
                      data-testid="admin-sell-row"
                      data-order-id={o.id}
                    >
                      <TableCell className="num py-2.5 text-xs">{o.id}</TableCell>
                      <TableCell className="py-2.5">
                        <p className="text-sm">{o.user_name}</p>
                        <p className="text-xs text-muted-foreground">{o.user_email}</p>
                      </TableCell>
                      <TableCell className="py-2.5">
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Icon className="h-3 w-3 text-muted-foreground" /> {M.label}
                        </span>
                        <p className="num mt-0.5 truncate text-[11px] text-muted-foreground" style={{ maxWidth: 160 }}>
                          {o.payout_method === "upi" && o.payout_details?.upi_id}
                          {o.payout_method === "bank" && `${o.payout_details?.account_number} · ${o.payout_details?.ifsc}`}
                          {o.payout_method === "imps_note" && (o.payout_details?.note || "").slice(0, 24)}
                        </p>
                      </TableCell>
                      <TableCell className="num py-2.5 text-right text-sm font-medium">{fmtMoney(o.amount_usdt, "USDT")}</TableCell>
                      <TableCell className="num py-2.5 text-right text-sm font-medium">{fmtMoney(o.inr_payable, "INR")}</TableCell>
                      <TableCell className="py-2.5"><StatusBadge status={o.status} /></TableCell>
                      <TableCell className="whitespace-nowrap py-2.5 text-xs text-muted-foreground">{fmtDate(o.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
      {data?.total > PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {page * PAGE + 1}–{Math.min(data.total, (page + 1) * PAGE)} of {data.total}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="admin-sell-prev">Previous</Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(page + 1) * PAGE >= data.total}
              onClick={() => setPage(page + 1)}
              data-testid="admin-sell-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}
      <DetailSheet id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
